import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const stats = [
  { value: "850+", label: "Realisierte Anlagen" },
  { value: "12.4 MWp", label: "Installierte Leistung" },
  { value: "25 J.", label: "Modulgarantie" },
  { value: "98 %", label: "Weiterempfehlung" },
];

const usps = [
  "Schweizer Fachpersonal, lokal verankert",
  "Swissolar-zertifizierte Solarteure",
  "Schlüsselfertige Komplettlösung inkl. Förderung",
  "10 Jahre Vollgarantie auf Installation",
  "Persönlicher Ansprechpartner – kein Call-Center",
  "24/7 Monitoring & Wartungs-Abo verfügbar",
];

export default function About() {
  return (
    <section
      id="ueber-uns"
      data-testid="about-section"
      className="relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="text-sm text-[#EAB308] font-semibold uppercase tracking-widest">
              Über Sonnenwerk
            </div>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D20]">
              Warum<br />
              <span className="text-[#4B5563]">Sonnenwerk?</span>
            </h2>
            <p className="mt-6 text-[#4B5563] text-base leading-relaxed">
              Seit über einem Jahrzehnt planen und installieren wir Photovoltaik-
              Anlagen in der ganzen Schweiz – vom Einfamilienhaus im Tessin bis
              zum Gewerbedach im Berner Oberland. Unser Anspruch: Schweizer
              Qualität, ehrliche Beratung und Anlagen, die jahrzehntelang
              zuverlässig Strom liefern.
            </p>

            <div className="mt-8 space-y-3">
              {usps.map((u, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3"
                  data-testid={`usp-${i}`}
                >
                  <CheckCircle2
                    size={20}
                    className="text-[#EAB308] mt-0.5 shrink-0"
                  />
                  <span className="text-[#1A1D20]/85">{u}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`stat-card-${i}`}
                  className={`relative rounded-2xl p-5 sm:p-7 md:p-10 border border-black/5 ${
                    i % 3 === 0
                      ? "bg-[#1A1D20] text-white"
                      : "bg-white text-[#1A1D20]"
                  } card-lift`}
                >
                  <div className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight">
                    {s.value}
                  </div>
                  <div
                    className={`mt-2 text-sm ${
                      i % 3 === 0 ? "text-white/70" : "text-[#4B5563]"
                    }`}
                  >
                    {s.label}
                  </div>
                  {i === 0 && (
                    <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-[#EAB308] glow-anim" />
                  )}
                </motion.div>
              ))}
            </div>

            <motion.figure
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="about-team-image"
              className="mt-4 relative rounded-2xl overflow-hidden img-placeholder"
            >
              <img
                src="/images/about-montage.webp"
                alt="Zwei Solarteure von Sonnenwerk montieren gesichert ein schwarzes Solarmodul auf einem Ziegeldach"
                width="1400"
                height="939"
                loading="lazy"
                decoding="async"
                className="w-full h-[260px] md:h-[300px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D20]/80 via-transparent to-transparent" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6">
                <div className="font-display font-semibold text-white text-lg leading-tight">
                  Eigene Montageteams – keine Subunternehmer.
                </div>
                <div className="mt-1 text-white/70 text-sm">
                  42 Solarteure an vier Standorten in der Deutsch- und Westschweiz.
                </div>
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  );
}
